<script setup lang="ts">
import { computed, nextTick, type Ref, ref } from 'vue';

import type { VAutocomplete } from 'vuetify/components';
import { storeToRefs } from 'pinia';
import gettext from '@/i18n/gettext';
import useNotionStore from '@/store/NotionStore';
import type { PreconfiguredRobot } from '@/models/NotionResources';
import useAuthStore from '@/store/AuthStore';
import type { SampleRobotConfig } from '@/models/Robot';

const { $gettext } = gettext;

const authStore = useAuthStore();
const notionStore = useNotionStore();

const emit = defineEmits<{
  (
    e: 'onSampleConfigSelected',
    sampleConfig: SampleRobotConfig | undefined,
  ): void;
}>();

const currentSampleConfiguration: Ref<SampleRobotConfig | undefined> =
  ref(undefined);

const sampleConfigSelector = ref();
const { preconfiguredRobots } = storeToRefs(notionStore);

function onSampleConfigSelected() {
  emit('onSampleConfigSelected', currentSampleConfiguration.value);

  // remove focus from sample config input
  nextTick(() => {
    (
      (sampleConfigSelector.value.$el as HTMLElement).getElementsByTagName(
        'input',
      )[0] as HTMLInputElement
    ).blur();
  });
}

const sampleConfigurations = computed(() => {
  return (preconfiguredRobots.value || [])
    .filter(
      (robot: PreconfiguredRobot) => robot.EnglishName !== 'Dispensing Doris',
    )
    .map((robot: PreconfiguredRobot) => {
      return {
        name: authStore.language === 'de' ? robot.RobotName : robot.EnglishName,
        moduleIds: robot.IdBuildOrder.split(', '),
        price: robot.ListPrice,
      };
    });
});
</script>
<template>
  <v-autocomplete
    ref="sampleConfigSelector"
    v-model="currentSampleConfiguration"
    item-value="moduleIds"
    item-title="name"
    :label="$gettext('Select sample configuration')"
    :items="sampleConfigurations"
    :return-object="true"
    @update:modelValue="onSampleConfigSelected"
  />
</template>
